// Next-Event Contact Service
// "VoteBeats at your next event?" form (ITER-200). Submissions go through a callable; only owner admins can read them.

import { FirebaseError } from 'firebase/app';
import { collection, DocumentData, limit, onSnapshot, orderBy, query, Timestamp, Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import {
  contactSentKey,
  type CrowdSize,
  type DjEventsPerYear,
  type EventTiming,
  type EventType,
  type NextEventContactSource,
  type PreferredContact,
  type Role,
} from '../utils/nextEventContact';

export interface NextEventContactParams {
  eventId: string | null;
  source: NextEventContactSource;
  role: Role | null;
  eventType: EventType | null;
  eventTiming: EventTiming | null;
  crowdSize: CrowdSize | null;
  djEventsPerYear: DjEventsPerYear | null;
  note: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  preferredContact: PreferredContact | null;
  ageConfirmed: boolean;
}

export type ContactEmailStatus = 'pending' | 'sent' | 'skipped' | 'held' | 'failed';

export interface NextEventContact {
  id: string;
  uid: string;
  eventId: string | null;
  eventName: string | null;
  eventUrl: string | null;
  source: NextEventContactSource;
  role: Role | null;
  eventType: EventType | null;
  eventTiming: EventTiming | null;
  crowdSize: CrowdSize | null;
  djEventsPerYear: DjEventsPerYear | null;
  note: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  preferredContact: PreferredContact | null;
  emailStatus: ContactEmailStatus;
  createdAt: Date | null;
  handledAt: Date | null;
}

const PASS_THROUGH_ERROR_CODES = ['functions/invalid-argument', 'functions/resource-exhausted'];

export async function submitNextEventContact(params: NextEventContactParams): Promise<{ alreadySent: boolean }> {
  const callable = httpsCallable<NextEventContactParams, { ok: boolean; alreadySent: boolean }>(functions, 'submitNextEventContact');
  const result = await callable(params);
  return { alreadySent: result.data.alreadySent };
}

export function contactErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError && PASS_THROUGH_ERROR_CODES.includes(error.code)) {
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

export async function markNextEventContactHandled(contactId: string, handled: boolean): Promise<void> {
  const callable = httpsCallable<{ contactId: string; handled: boolean }, { ok: boolean }>(functions, 'markNextEventContactHandled');
  await callable({ contactId, handled });
}

function toDate(value: unknown): Date | null {
  return value instanceof Timestamp ? value.toDate() : null;
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

export function docToNextEventContact(id: string, data: DocumentData): NextEventContact {
  return {
    id,
    uid: data.uid ?? '',
    eventId: toNullableString(data.eventId),
    eventName: toNullableString(data.eventName),
    eventUrl: toNullableString(data.eventUrl),
    source: data.source ?? 'direct',
    role: data.role ?? null,
    eventType: data.eventType ?? null,
    eventTiming: data.eventTiming ?? null,
    crowdSize: data.crowdSize ?? null,
    djEventsPerYear: data.djEventsPerYear ?? null,
    note: toNullableString(data.note),
    name: data.name ?? '',
    email: toNullableString(data.email),
    phone: toNullableString(data.phone),
    preferredContact: data.preferredContact ?? null,
    emailStatus: data.emailStatus ?? 'pending',
    createdAt: toDate(data.createdAt),
    handledAt: toDate(data.handledAt),
  };
}

export function subscribeToNextEventContacts(
  onChange: (contacts: NextEventContact[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const contactsQuery = query(collection(db, 'nextEventContacts'), orderBy('createdAt', 'desc'), limit(200));
  return onSnapshot(
    contactsQuery,
    (snapshot) => onChange(snapshot.docs.map((contactDoc) => docToNextEventContact(contactDoc.id, contactDoc.data()))),
    onError
  );
}

export function hasSentContact(eventId: string | null): boolean {
  try {
    return localStorage.getItem(contactSentKey(eventId)) !== null;
  } catch (error) {
    console.warn('Next-event contact: localStorage is unavailable', error);
    return false;
  }
}

export function rememberSentContact(eventId: string | null): void {
  try {
    localStorage.setItem(contactSentKey(eventId), new Date().toISOString());
  } catch (error) {
    console.warn('Next-event contact: could not remember the submission in localStorage', error);
  }
}
